/**
 * Whether a workspace can be signed in to, as one rule.
 *
 * It is the same rule the API applies at login (`isTenantUsable`): a workspace
 * is usable only while it is active **and** its subscription has not lapsed.
 * Two conditions, three outcomes, and the three are not interchangeable —
 * "Suspended" is a decision somebody made and can undo from the detail screen,
 * "Expired" is a date that passed and needs a new one.
 *
 * WHY IT MOVED OUT OF `TenantsPage`. It lived there while the list was the only
 * screen that showed a workspace's state. The detail screen shows the same
 * state, next to the two controls that change it, and a second implementation
 * would eventually disagree with the first — the way it would disagree is that
 * the list would say Active and the detail screen would say Expired, or worse,
 * both would say Active for a workspace whose users cannot sign in. A staff
 * member reading two different answers on two screens has no way to tell which
 * one the API believes.
 *
 * It takes the fields rather than a `Tenant`, because the list reads
 * `TenantSummary` and the detail screen reads `Tenant`, and the rule needs
 * exactly two of the fields both of them carry.
 */
import type { BadgeTone } from '@wend/ui';

export interface SubscriptionState {
  readonly label: string;
  /** A status tone only — `good`, `warn` or `bad` — drawn by `@wend/ui`'s Badge. */
  readonly tone: BadgeTone;
  /** One sentence for a screen with room for it. Empty on the list. */
  readonly detail: string;
}

export interface TenantSubscriptionFacts {
  readonly isActive: boolean;
  readonly validTill: string;
}

export function subscriptionState(tenant: TenantSubscriptionFacts): SubscriptionState {
  if (!tenant.isActive) {
    return {
      label: 'Suspended',
      tone: 'warn',
      detail:
        'Nobody in this workspace can sign in. Its data, campaigns and credits are untouched, and restoring access is a tick box on this screen.',
    };
  }

  if (Date.parse(tenant.validTill) <= Date.now()) {
    return {
      label: 'Expired',
      tone: 'bad',
      detail:
        'The subscription has lapsed, so nobody in this workspace can sign in. Nothing has been deleted — moving the expiry date forward restores access.',
    };
  }

  return {
    label: 'Active',
    tone: 'good',
    detail: 'This workspace is signed in to normally.',
  };
}
